import { PlusOutlined } from '@ant-design/icons';
import { FormHandler } from '@framework/form/form/FormHandler.tsx';
import { Button, Divider, Layout, Select, Space, Typography } from 'antd';
import { useMemo } from 'react';
import { useTheme } from 'styled-components';
import { object, string } from 'yup';
import { type ComputeExpressionReferenceAttributes } from '../../../../schema/expressions/compute/input/nodes/ComputeExpressionReferenceNode.tsx';
import { ReferenceType } from '../../../../schema/expressions/references/IReference.ts';
import { ReferencePathDirection } from '../../../../schema/expressions/references/IReferencePath.ts';
import { buildPlaceholderCode } from './buildPlaceholderCode.ts';
import { PlaceholderCategoryTitle } from './PlaceholderCategoryTitle.tsx';
import { PlaceholderCodePreview } from './PlaceholderCodePreview.tsx';
import { PlaceholderParameterInput } from './PlaceholderParameterInput.tsx';
import { PlaceholderTypeIcon } from './PlaceholderTypeIcon.tsx';
import {
  mapPlaceholderTypeToField,
  type PlaceholderCategory,
  type PlaceholderReference,
} from './types.ts';

interface Props {
  category?: PlaceholderCategory;
  fields?: PlaceholderReference[];
  onClose: () => void;
  onInsert: (reference: ComputeExpressionReferenceAttributes) => void;
  selectedCode: string | undefined;
  setSelectedCode: (code: string | undefined) => void;
}

export function PlaceholderCategoryFields(props: Props) {
  const { category, fields, onClose, onInsert, selectedCode, setSelectedCode } =
    props;

  const selectedField = useMemo(() => {
    return fields?.find(field => field.code === selectedCode);
  }, [fields, selectedCode]);
  const theme = useTheme();

  const schema = useMemo(() => {
    return object(
      Object.fromEntries(
        (selectedField?.parameters ?? []).map(param => [
          param.id,
          string()
            .transform((_, originalValue) =>
              originalValue == null ? '' : String(originalValue),
            )
            .trim()
            .required(),
        ]),
      ),
    );
  }, [selectedField]);

  const initialValues = useMemo(() => {
    return Object.fromEntries(
      (selectedField?.parameters ?? []).map(p => [p.id, '']),
    );
  }, [selectedField]);

  const placeholderOptions = useMemo(() => {
    return [...(fields ?? [])]
      .sort((a, b) => a.label.localeCompare(b.label))
      .map(field => ({
        label: (
          <Space>
            <PlaceholderTypeIcon type={field.type} />
            {field.label}
          </Space>
        ),
        rawLabel: field.label,
        value: field.code,
      }));
  }, [fields]);

  const handleInsert = (data: Record<string, string>) => {
    if (!selectedField) return;

    const code = buildPlaceholderCode(selectedField, data);

    const field = mapPlaceholderTypeToField(selectedField.type);
    const reference: ComputeExpressionReferenceAttributes = {
      id: `placeholder_${code}`,
      type: field.type,
      label: code,
      reference: {
        code,
        id: `placeholder_${code}`,
        label: code,
        type: ReferenceType.Placeholder,
        path: [
          {
            direction: ReferencePathDirection.Absolute,
            code: 'placeholders',
          },
        ],
        field,
        group: category ?? 'Placeholder',
      },
    };
    onInsert(reference);
  };

  return (
    <Layout>
      <Layout.Header
        style={{
          padding: '12px',
          height: 'auto',
          lineHeight: 'unset',
          borderBottom: `1px solid ${theme.colorBorderSecondary}`,
        }}
      >
        {category && <PlaceholderCategoryTitle category={category} />}
      </Layout.Header>
      <FormHandler
        schema={schema}
        initialValues={initialValues}
        onSubmit={handleInsert}
        hideVisualFeedback
        nested
        disableEnterSubmit
        disableReinitialize
      >
        <Layout.Content
          style={{ padding: '12px', background: theme.colorBgContainer }}
        >
          <Select
            style={{ width: '100%' }}
            size="large"
            value={selectedCode}
            showSearch
            optionFilterProp="rawLabel"
            optionLabelProp="label"
            onChange={setSelectedCode}
            options={placeholderOptions}
            placeholder="Seleziona un placeholder"
            getPopupContainer={trigger => trigger.parentElement}
          />
          {selectedField && (
            <>
              {selectedField.description && (
                <Typography.Text>{selectedField.description}</Typography.Text>
              )}
              {selectedField.parameters.length > 0 && (
                <>
                  <Divider orientation="left">Parametri</Divider>
                  {selectedField.parameters.map(param => (
                    <PlaceholderParameterInput
                      key={param.id}
                      parameter={param}
                    />
                  ))}
                </>
              )}
              <PlaceholderCodePreview selectedField={selectedField} />
            </>
          )}
        </Layout.Content>
        <Layout.Footer
          style={{
            padding: '12px',
            height: 'auto',
            lineHeight: 'unset',
            borderTop: `1px solid ${theme.colorBorderSecondary}`,
            display: 'flex',
            background: theme.colorBgContainer,
            alignItems: 'end',
          }}
        >
          <Space style={{ marginLeft: 'auto' }}>
            <Button onClick={onClose}>Annulla</Button>
            <Button
              icon={<PlusOutlined />}
              type="primary"
              disabled={!selectedCode}
              htmlType="submit"
            >
              Inserisci
            </Button>
          </Space>
        </Layout.Footer>
      </FormHandler>
    </Layout>
  );
}
